// piano-frontend/src/components/MakeupDecisionDialog.js
// 고정 스케줄 변경 시 '이미 사용된 보강 티켓' 처리 방법을 관리자가 선택하는 다이얼로그.
// 백엔드가 409(requiresMakeupDecision)로 내려준 decision 데이터를 받아 두 가지 중 선택:
//   ① 이미 잡아둔 보강(수동) 예약을 제거 → 새 스케줄은 전부 생성 (티켓 자동 정산)
//   ② 보강 예약 유지 → 새 스케줄 생성분에서 필요한 개수만큼 날짜 제외
// 정확히 필요한 개수(deficit)만큼 선택해야 확정 버튼이 활성화된다.
import React, { useEffect, useState } from "react";
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Alert, Radio, RadioGroup, FormControlLabel, Checkbox, Chip, CircularProgress
} from "@mui/material";

const KO_DAYS = ["일", "월", "화", "수", "목", "금", "토"];
const DAY_LABEL = { MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토", SUN: "일" };

function fmtDate(dateStr) {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()} (${KO_DAYS[d.getDay()]})`;
}

// active: keep-alive 페이지가 숨겨질 때 함께 숨기기 위한 표시용 플래그.
// 의미상의 open과 분리되어 있어 숨김/복원 시 초기화 이펙트([open, decision])가 다시 돌지 않는다.
export default function MakeupDecisionDialog({ open, active = true, decision, onCancel, onConfirm, submitting = false }) {
  const [mode, setMode] = useState("REMOVE_MANUAL");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);

  const deficit = decision?.deficit || 0; // 총 선택 필요 수
  const ticketShortfall = decision?.ticketShortfall ?? decision?.deficit ?? 0;
  const manual = decision?.manualCandidates || [];
  const planned = decision?.plannedDates || [];
  const used = decision?.usedRows || [];
  const stale = decision?.staleExcluded || [];

  useEffect(() => {
    if (open) {
      // 수동 예약 후보가 부족하면 제외 모드로 시작
      setMode((decision?.manualCandidates || []).length >= (decision?.deficit || 0) ? "REMOVE_MANUAL" : "DROP_GENERATED");
      setSelectedIds([]);
      setSelectedKeys([]);
    }
  }, [open, decision]);

  if (!decision) return null;

  const manualPossible = manual.length >= deficit;
  const count = mode === "REMOVE_MANUAL" ? selectedIds.length : selectedKeys.length;
  const canConfirm = count === deficit;

  const toggleIn = (list, setList, v) => {
    if (list.includes(v)) {
      setList(list.filter((x) => x !== v));
    } else if (list.length < deficit) {
      setList([...list, v]);
    }
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(
      mode === "REMOVE_MANUAL"
        ? { mode: "REMOVE_MANUAL", reservation_ids: selectedIds }
        : { mode: "DROP_GENERATED", drop_keys: selectedKeys }
    );
  };

  const CountChip = ({ n }) => (
    <Chip
      size="small"
      color={n === deficit ? "success" : "default"}
      label={`${n} / ${deficit} 선택됨`}
      sx={{ fontWeight: 700 }}
    />
  );

  return (
    <Dialog open={open && active} onClose={submitting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>보강 처리 방법을 선택해주세요</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="warning">
            <b>{decision.member_name}</b> 회원의 새 스케줄을 이대로 전부 생성하면 수업이{" "}
            <b>{deficit}회 초과 제공</b>됩니다.
          </Alert>

          {ticketShortfall > 0 && (
            <Typography variant="body2" color="text.secondary">
              · 이전 스케줄에서 발행된 티켓(자리 없음 보강 / 취소·휴일 환불) 중{" "}
              <b>{ticketShortfall}장이 이미 사용</b>되었습니다 (다른 시간에 보강·변경 예약을
              잡으신 것으로 보입니다).
              {used.filter((u) => u.reason !== "CANCELLED" && u.reason !== "HOLIDAY").length > 0 && (
                <>
                  {" "}자리가 없었던 날짜:{" "}
                  {used
                    .filter((u) => u.reason !== "CANCELLED" && u.reason !== "HOLIDAY")
                    .map((u) => `${fmtDate(u.date)} ${u.start_time}`)
                    .join(", ")}
                </>
              )}
              {used.filter((u) => u.reason === "CANCELLED" || u.reason === "HOLIDAY").length > 0 && (
                <>
                  {" "}취소·휴일 환불 주차:{" "}
                  {used
                    .filter((u) => u.reason === "CANCELLED" || u.reason === "HOLIDAY")
                    .map((u) => `${fmtDate(u.date)} ${u.start_time}`)
                    .join(", ")}
                </>
              )}
            </Typography>
          )}

          {stale.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              · 이전에 <b>&lsquo;새 일정에서 제외&rsquo; 또는 &lsquo;티켓 반환 없이
              취소&rsquo;로 처리했던 주차 {stale.length}건</b>(
              {stale.map((s) => `${fmtDate(s.date)} ${s.start_time}`).join(", ")})은 일정이 바뀌어
              제외할 날짜를 다시 정해야 합니다.
            </Typography>
          )}

          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            {/* ① 보강(수동) 예약 제거 */}
            <FormControlLabel
              value="REMOVE_MANUAL"
              control={<Radio />}
              disabled={!manualPossible}
              label={
                <Box>
                  <Typography fontWeight={700}>
                    이미 잡아둔 보강(수동) 예약을 제거합니다
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    선택한 예약을 삭제하고 새 스케줄은 전부 생성합니다. 티켓은 자동 정산되어 잔액
                    변동이 없습니다.
                    {!manualPossible && " (제거할 수동 예약이 부족해 선택할 수 없습니다)"}
                  </Typography>
                </Box>
              }
            />
            {mode === "REMOVE_MANUAL" && manualPossible && (
              <Box sx={{ pl: 4, py: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography variant="body2" fontWeight={600}>제거할 예약 선택</Typography>
                  <CountChip n={selectedIds.length} />
                </Stack>
                <Stack sx={{ maxHeight: 200, overflowY: "auto" }}>
                  {manual.map((m) => (
                    <FormControlLabel
                      key={m.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(m.id)}
                          onChange={() => toggleIn(selectedIds, setSelectedIds, m.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {fmtDate(m.date)} {m.start_time}~{m.end_time}
                          {m.teacher_name ? ` · ${m.teacher_name}` : ""}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* ② 보강 유지 + 새 일정에서 제외 */}
            <FormControlLabel
              value="DROP_GENERATED"
              control={<Radio />}
              label={
                <Box>
                  <Typography fontWeight={700}>
                    보강 예약은 그대로 두고, 새 스케줄에서 {deficit}개 날짜를 뺍니다
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    선택한 날짜에는 레슨을 만들지 않습니다. 그 주 수업은 이미 보강으로 제공된
                    것으로 처리됩니다.
                  </Typography>
                </Box>
              }
            />
            {mode === "DROP_GENERATED" && (
              <Box sx={{ pl: 4, py: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography variant="body2" fontWeight={600}>제외할 날짜 선택</Typography>
                  <CountChip n={selectedKeys.length} />
                </Stack>
                <Stack sx={{ maxHeight: 200, overflowY: "auto" }}>
                  {planned.map((p) => (
                    <FormControlLabel
                      key={p.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedKeys.includes(p.key)}
                          onChange={() => toggleIn(selectedKeys, setSelectedKeys, p.key)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {fmtDate(p.date)} ({DAY_LABEL[p.day] || p.day}) {p.start_time}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onCancel} color="inherit" disabled={submitting}>취소 (스케줄 변경 중단)</Button>
        <Button
          variant="contained"
          disabled={!canConfirm || submitting}
          onClick={handleConfirm}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? "적용 중..." : canConfirm ? "이대로 적용" : `${deficit - count}개 더 선택해주세요`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
